/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
/* eslint-disable no-multi-spaces */
import isNil from 'lodash.isnil';
import {getAssetState} from '@ion/core/src/entities/Asset';
import {CVSAssetType} from '@ion/core/src/types/entities';
import {hexToRgb} from '@ion/core/src/utils/StringUtil';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {ColorModeEnum} from '@ion/dls/src/types/types';
import {UpdaterProps} from '../layers/AssetLayer';
import {EndIcon} from '../symbols/BaseSymbols';
import ClusterSymbol from '../symbols/ClusterSymbol';
import PointSymbol, {EndIconType} from '../symbols/PointSymbol';

/**
 * Defines the zoom breakpoint at which the marker modes are based. When below
 * the given breakpoint, the default (icon only) markers will be rendered,
 * otherwise, the detailed (status, label, and icon) markers will be rendered.
 */
export const MARKER_MODE_BREAKPOINT = 15;

/**
 * Defines the MarkerMode which determine the type of asset marker to be displayed,
 * either in default mode (icon marker only), or in detailed mode (status, label,
 * and icon based markers).
 */
export enum MarkerMode {
  default  = 'default',
  detailed = 'detailed'
};

/**
 * Defines the AssetMarkerStates which are used to determine the icon which
 * is to be rendered based on the state of the asset's marker within the map.
 */
export enum AssetMarkerStates {
  DEFAULT  = 'Default',
  SELECTED = 'Selected'
};

export enum MarkerArrowColor {
  Active = 'caGreen',
  Moving = 'caGreen',
  Parked = 'grey4',
  Idle   = 'caYellow',
  Towed  = 'caRed'
}

export interface LabelContentClient {
  isLabellingActive?: boolean;
  labelContent?: string;
  defaultlabel?: string;
  onChangeLabelContent?: Function;
};

export interface CreateMarkerParams extends LabelContentClient {
  asset?: CVSAssetType,
  Graphic: any,
  mode?: MarkerMode,
  color?: any,
  selectedAsset?: any,
  markerOnly?: boolean
};

export interface CreateMarkersParams extends CreateMarkerParams {
  assets: CVSAssetType[]
};

/**
 * Defines the default custom images used for the ClusterMarker. Applications
 * which use the ClusterMarker component are to have the following assets available
 * from their root path if the given props have not been provided:
 *
 *   'assets/maps/cluster-double-dark.png';
 *   'assets/maps/cluster-double-light.png';
 *   'assets/maps/cluster-single-dark.png';
 *   'assets/maps/cluster-single-light.png';
 */
export const DoubleDarkImgURL  = 'assets/maps/cluster-double-dark.png';
export const DoubleLightImgURL = 'assets/maps/cluster-double-light.png';
export const SingleDarkImgURL  = 'assets/maps/cluster-single-dark.png';
export const SingleLightImgURL = 'assets/maps/cluster-single-light.png';

// provides a utility which resolves the cluster image url to be used ...
export const getImgURL = (single: boolean, color: string) => {
  const isDarkMode = color === 'dark';
  if (single) {
    return isDarkMode ? SingleDarkImgURL : SingleLightImgURL;
  }
  return isDarkMode ? DoubleDarkImgURL : DoubleLightImgURL;
};

const getCoordinates = (asset: CVSAssetType) => {
  const {geometry} = asset;
  return geometry?.coordinates || geometry;
};

export const getEndIcon = (asset: CVSAssetType) => {
  const {
    evBatteryPackRemainingCharge,
    evDerivedChargingState
  } = asset;
  const evCharge = !isNil(evBatteryPackRemainingCharge)
    ? evBatteryPackRemainingCharge / 10
    : null;
  const evIsCharging = !isNil(evDerivedChargingState) &&
    (evDerivedChargingState.toUpperCase() === 'ON');
  if (evIsCharging) {
    return {
      type  : EndIconType.CHARGING,
      color : hexToRgb(dlsColor('caGreen'))
    } as EndIcon;
  } else if (!isNil(evCharge)) {
    let type: string;
    let color: number[];
    if (evCharge === 100) {
      type  = EndIconType.BATTERY_FULL;
      color = hexToRgb(dlsColor('caGreen'));
    } else if (evCharge > 75) {
      type  = EndIconType.BATTERY_HIGH;
      color = hexToRgb(dlsColor('caGreen'));
    } else if (evCharge > 50) {
      type  = EndIconType.BATTERY_HALF;
      color = hexToRgb(dlsColor('caGreen'));
    } else if (evCharge > 25) {
      type  = EndIconType.BATTERY_HALF;
      color = hexToRgb(dlsColor('caYellow'));
    } else if (evCharge > 0) {
      type  = EndIconType.BATTERY_LOW;
      color = hexToRgb(dlsColor('caRed'));
    } else {
      type  = EndIconType.BATTERY_EMPTY;
      color = hexToRgb(dlsColor('caRed'));
    }
    return {type, color} as EndIcon;
  }
  return null;
};

export const getAssetColor = (asset, defaultColor = 'grey3') => {
  const assetState  = getAssetState(asset);
  const assetStatus = assetState.length ? MarkerArrowColor[assetState] : defaultColor;
  return dlsColor(assetStatus || defaultColor);
};

/**
 * Updates the provided asset marker to either the default state or the selected
 * state based on the value of isHover.
 */
export const updateMarker = ({
  asset,
  graphic,
  onHoveredPanelAsset,
  hoveredAsset,
  markerOnly,
  isLabellingActive,
  labelContent,
  defaultlabel,
  isHover = false,
  mode = MarkerMode.default
}: UpdaterProps) => {
  if (onHoveredPanelAsset) {
    if (isHover && asset?.assetId !== hoveredAsset?.assetId) {
      onHoveredPanelAsset(asset);
    }
  }
  const title       = asset[labelContent] ?? defaultlabel;
  graphic.symbol = {
    type : 'cim',
    data : {
      type   : 'CIMSymbolReference',
      symbol : PointSymbol.createPointMarker({
        coordinates      : getCoordinates(hoveredAsset ?? asset),
        arrowColor       : hexToRgb(getAssetColor(asset)),
        arrowCircleColor : hexToRgb(dlsColor(isHover ? 'black' : 'grey8')),
        heading          : asset.heading,
        assetType        : asset.assetType,
        isSelected       : isHover,
        endIcon          : getEndIcon(asset),
        markerOnly,
        isLabellingActive,
        title
      })
    }
  };
};

// Utility to return color based on given params.
const getColor = (asset: CVSAssetType, color: ColorModeEnum) => {
  const {geometry} = asset || {};
  const isNoGPS = !geometry?.coordinates?.[0] && !geometry?.coordinates?.[1];
  return !isNoGPS
    ? color !== ColorModeEnum.dark ? dlsColor('white') : dlsColor('aaBlue')
    : dlsColor('caOrange');
};

/**
 * Creates an individual Graphic objects based on the given assets, where
 * the Graphic depicts the asset in it's current location.
 *
 * ```typescript
 *
 *   import {createMarker} from '@ion/dls/src/maps/markers';
 *
 *   loadModules(['esri/layers/GraphicsLayer', 'esri/Graphic'])
 *     .then(([GraphicsLayer, Graphic]) => {
 *        const graphic = createMarker({asset, Graphic});
 *        ...
 *     })
 *     ...
 *
 * ```
 *
 */
export const createMarker = ({
  asset,
  Graphic,
  mode,
  color,
  selectedAsset,
  markerOnly,
  isLabellingActive,
  defaultlabel,
  labelContent = 'name'
}: CreateMarkerParams) => {
  if (Graphic) {
    if (asset?.type === 'Feature') {
      const {geometry, properties} = asset;
      const isSmallSize  = String(properties?.point_count)?.length < 3;
      const clusterColor = getColor(asset, color);
      return new Graphic({
        geometry : {
          type      : 'point',
          latitude  : geometry.coordinates[1],
          longitude : geometry.coordinates[0]
        },
        attributes : {...asset},
        symbol     : {
          type : 'cim',
          data : {
            type   : 'CIMSymbolReference',
            symbol : ClusterSymbol.createClusterMarker(isSmallSize, hexToRgb(clusterColor))
          }
        }
      });
    } else {
      const {geometry, heading, assetType} = asset;
      const title = asset[labelContent] ?? defaultlabel;
      const isSameAsset = asset.assetId === selectedAsset?.assetId;
      const [longitude, latitude] = geometry.coordinates;
      const assetStatus = MarkerArrowColor[getAssetState(asset)];
      return new Graphic({
        geometry : {
          type : 'point',
          latitude,
          longitude
        },
        attributes : {...asset},
        symbol     : {
          type : 'cim',
          data : {
            type   : 'CIMSymbolReference',
            symbol : PointSymbol.createPointMarker({
              arrowColor       : hexToRgb(dlsColor(assetStatus ?? 'grey3')),
              arrowCircleColor : hexToRgb(dlsColor(isSameAsset ? 'black': 'grey8')),
              isSelected       : isSameAsset,
              endIcon          : getEndIcon(asset),
              title,
              heading,
              markerOnly,
              isLabellingActive,
              assetType
            })
          }
        }
      });
    }
  }
};

export const createTextGraphic = (asset: CVSAssetType, Graphic: any, color) => {
  if (Graphic) {
    if (asset?.type === 'Feature') {
      const {geometry} = asset;
      return new Graphic({
        geometry : {
          type      : 'point',
          latitude  : geometry.coordinates[1],
          longitude : geometry.coordinates[0]
        },
        attributes : {...asset},
        symbol     : {
          type    : 'text',
          color   : color === 'dark' ? dlsColor('white') : dlsColor('grey1'),
          text    : `${asset?.properties?.point_count}`,
          xoffset : 0,
          yoffset : -4,
          font    : {
            size : 11
          }
        }
      });
    }
  }
};

/**
 * Creates a collection of Graphics objects based on a collection of assets,
 * where each Graphic depicts an individual asset in it's current location.
 *
 * ```typescript
 *
 *   import {createMarkers} from '@ion/dls/src/maps/markers';
 *
 *   loadModules(['esri/layers/GraphicsLayer', 'esri/Graphic'])
 *     .then(([GraphicsLayer, Graphic]) => {
 *        const graphics = createMarkers(assets, Graphic);
 *        const layer = new GraphicsLayer({graphics});
 *        ...
 *     })
 *     ...
 *
 * ```
 *
 */
export const createMarkers = ({
  assets,
  Graphic,
  color,
  markerOnly,
  labelContent,
  defaultlabel,
  isLabellingActive = true,
  mode = MarkerMode.detailed,
  selectedAsset = null
}: CreateMarkersParams) => {
  if (Graphic) {
    const clusterAssets = [];
    const markerAssets  = [];
    const textGraphic = [];
    assets?.forEach(asset => {
      if (asset?.type === 'Feature') {
        clusterAssets.push(createMarker({asset, Graphic, mode, color, labelContent, defaultlabel}));
      } else {
        markerAssets.push(createMarker({
          asset, Graphic, mode, color, selectedAsset, markerOnly, isLabellingActive, labelContent, defaultlabel
        }));
      }
      const assetGraphicText = createTextGraphic(
        asset,
        Graphic,
        color
      );
      if (assetGraphicText) {
        textGraphic.push(assetGraphicText);
      }
    });
    return [...clusterAssets, ...textGraphic, ...markerAssets];
  }
};

export default createMarkers;
